import { useEffect, useState, useRef } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import useRegistration from '@/hooks/registration/useRegistration';
import {
  useMultiKitValidationKitMutation,
  useRegisterKitQuery,
} from '@viome/viome-base/src/usecase/Register/RegisterQuery';
import * as P from '@/constants/paths';
import { useUserStore } from '../useUserStore';
import { useGetUserProfileQuery } from '@viome/viome-base/src/usecase/Login/UserQuery';
import useRegisterStore, { PhoneCode, RegistrationTrack } from '../useRegistrationStore';
import { getIsoString } from '@/components/utils/utils';
import { useNavigate } from 'react-router-dom';

export const maskValue = (input: string) => {
  return input
    .toUpperCase() // Convert to uppercase
    .replace(/[^A-Z0-9]/g, '') // Remove non-alphanumeric characters
    .slice(0, 12) // Limit to 12 characters
    .replace(/(.{4})(.{0,4})(.{0,4})/, (_match, p1, p2, p3) => {
      return [p1, p2, p3].filter(Boolean).join('-');
    });
};

export const deSanitizedKitId = (kitId: string) => {
  return kitId.replace(/-/g, '');
};

const checkForDisableNextAction = (kitId: string) => {
  if (kitId.length === 14) {
    return false;
  } else {
    return true;
  }
};

const defaultSchema = z.object({
  kitId: z.string().refine(
    (value) => {
      const masked = maskValue(value);
      return masked === value;
    },
    {
      message: 'kitId must match the masked format (e.g., XXXX-XXXX-XXXX).',
    },
  ),
  // Add other fields as needed
  otherField: z.string().optional(), // Example additional field
});

export enum RegisterInterrupts {
  CdKit = 'cdKit',
  TrialBundle = 'isTrialBundle',
}

const useRegistrationKitId = () => {
  const [shouldFetchKitData, setShouldFetchKitData] = useState(false);
  const [showRegisterInterrupt, setShowRegisterInterrupt] =
    useState<RegisterInterrupts | null>(null);
  const [errorMessageStr, setErrorMessageStr] = useState('');
  const hasCheckedMultiKit = useRef(false);
  const { refetch: getUserDetails } = useGetUserProfileQuery({
    enabled: false,
  });
  const {
    setAddressDetails,
    setPersonalInformation,
    setAccountCreation,
    setRestrictedRegistrationSteps,
    setOffers
  } = useRegisterStore();
  const { kitId, setKitId, navigateWithParams } = useRegistration();
  const [disableNext, setDisableNext] = useState(
    checkForDisableNextAction(kitId),
  );
  const { user: userData } = useUserStore();
  const navigation = useNavigate();

  const form = useForm<z.infer<typeof defaultSchema>>({
    mode: 'onSubmit',
    resolver: zodResolver(defaultSchema),
    defaultValues: {
      kitId: kitId ?? undefined,
    },
  });

  const formOnValid = (formValues: { kitId: string }) => {
    setShouldFetchKitData(true);
    setKitId(formValues.kitId);
    hasCheckedMultiKit.current = false;
  };

  const { setValue } = form;

  const handleChange = (e: { target: { value: string } }) => {
    const inputValue = e.target.value;
    setDisableNext(checkForDisableNextAction(maskValue(inputValue)));

    if (inputValue.length > 14) {
      return;
    }

    const masked = maskValue(inputValue);
    setValue('kitId', masked);
    setErrorMessageStr('');
  };

  const configureOnChange = (e: { target: { value: string } }) => {
    handleChange(e);
    setErrorMessageStr('');
  };

  const {
    isLoading: isRegisterkitQueryLoading,
    refetch,
    data: registerKitQuery,
    error: APIError,
  } = useRegisterKitQuery(deSanitizedKitId(kitId) ?? '');

  const { isPending: isLoadingMultiKit, mutate: multiKitMutation } =
    useMultiKitValidationKitMutation();
  const isKitQueryLoading = isRegisterkitQueryLoading || isLoadingMultiKit;

  const kidIdFromRemote: string = registerKitQuery?.kitId ?? '';

  const checkMultiKitValidation = (id: string) => {
    if (hasCheckedMultiKit.current) return;
    hasCheckedMultiKit.current = true;

    multiKitMutation(
      { kitId: deSanitizedKitId(id) },
      {
        onSuccess: async (response) => {
          if (response?.registrationAllowed) {
            const userDetails = await getUserDetails();
            const addressDetails =
              userDetails.data?.address ||
              userDetails.data?.latestAddressWithKitId;
            const smsConsent = userDetails?.data?.smsConsent;
            const contactInfo = userDetails?.data;
            const truncatedPhoneCode: PhoneCode = {
              countryPhoneCode: contactInfo?.countryPhoneCode ?? '',
              countryCode: addressDetails?.country ?? '',
            };

            contactInfo &&
              addressDetails &&
              setAddressDetails({
                country: addressDetails?.country,
                street: addressDetails?.addressLine1,
                apartment: addressDetails?.addressLine2 ?? '',
                city: addressDetails?.city,
                phoneCode: truncatedPhoneCode,
                phone: contactInfo?.phone ?? '',
                state: addressDetails?.state,
                zip: addressDetails?.zip,
              });
            contactInfo &&
              setPersonalInformation(
                contactInfo?.firstName,
                contactInfo?.lastName,
                getIsoString(contactInfo?.birthDate),
                contactInfo?.gender,
              );
            setOffers(smsConsent ?? false);
            contactInfo && setAccountCreation(contactInfo?.email, '');
            setShowRegisterInterrupt(null);
            Promise.all([
              setRestrictedRegistrationSteps([
                RegistrationTrack.AccountCreation,
                RegistrationTrack.PersonalInformation,
                RegistrationTrack.AddressConfirmation,
                RegistrationTrack.RegistrationConfirmation,
                RegistrationTrack.TermsOfService,
                RegistrationTrack.ConsentForm,
              ]),
              navigateWithParams(P.REGISTRATION_CONTACT_INFORMATION),
            ]);
          } else {
            if (response?.message === 'ANOTHER_KIT_IS_ACTIVE') {
              navigation(P.REGISTRATION_PENDING_KIT);
            }
          }
        },
        onError: (multiKitError) => {
          setErrorMessageStr(
            multiKitError?.message ||
              'An error occurred during multi-kit validation.',
          );
        },
      },
    );
  };

  useEffect(() => {
    if (kitId && shouldFetchKitData) {
      refetch();
    }
  }, [kitId, refetch, shouldFetchKitData]);

  useEffect(() => {
    if (registerKitQuery?.kitId && !hasCheckedMultiKit.current) {
      setShouldFetchKitData(false);
      if (registerKitQuery?.cdKit || registerKitQuery?.isTrialBundle) {
        const registerInterrupt = registerKitQuery?.cdKit
          ? RegisterInterrupts.CdKit
          : RegisterInterrupts.TrialBundle;
        setShowRegisterInterrupt(registerInterrupt);
      } else {
        if (userData) {
          checkMultiKitValidation(kidIdFromRemote);
        } else {
          Promise.all([
            setRestrictedRegistrationSteps([
              RegistrationTrack.ContactInformation,
              RegistrationTrack.PersonalInformation,
              RegistrationTrack.AddressConfirmation,
              RegistrationTrack.RegistrationConfirmation,
              RegistrationTrack.TermsOfService,
              RegistrationTrack.ConsentForm,
            ]),
            navigateWithParams(P.REGISTRATION_ACCOUNT_CREATION),
          ]);
        }
      }
    } else if (registerKitQuery?.errorMessage) {
      setErrorMessageStr(registerKitQuery?.errorMessage);
    }
  }, [registerKitQuery]);

  useEffect(() => {
    if (APIError) {
      setErrorMessageStr(
        APIError.message || 'An error occurred while fetching kit data.',
      );
    }
  }, [APIError]);

  return {
    form,
    formOnValid,
    handleChange,
    errorMessageStr,
    isKitQueryLoading,
    configureOnChange,
    showRegisterInterrupt,
    disableNext,
    checkMultiKitValidation,
    kidIdFromRemote,
  };
};

export default useRegistrationKitId;
